const dateTimeFormatOptions: Intl.DateTimeFormatOptions = {
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hourCycle: "h23",
};

const dateTimeFormatters = new Map<string, Intl.DateTimeFormat | null>();

const getDateTimeFormatter = (timezoneId: string) => {
  if (dateTimeFormatters.has(timezoneId)) {
    return dateTimeFormatters.get(timezoneId) ?? null;
  }

  try {
    const formatter = new Intl.DateTimeFormat("en-US", {
      ...dateTimeFormatOptions,
      timeZone: timezoneId,
    });

    dateTimeFormatters.set(timezoneId, formatter);
    return formatter;
  } catch {
    dateTimeFormatters.set(timezoneId, null);
    return null;
  }
};

const getDatePart = (parts: Intl.DateTimeFormatPart[], type: Intl.DateTimeFormatPartTypes) => {
  return Number(parts.find((part) => part.type === type)?.value);
};

const hasExplicitTimezone = (date: string) => /(Z|[+-]\d{2}:?\d{2})$/i.test(date);

const parseUtcInstant = (date: Date | string) => {
  const value = typeof date === "string" && !hasExplicitTimezone(date) ? `${date}Z` : date;
  const instant = new Date(value);

  return Number.isNaN(instant.getTime()) ? null : instant;
};

const padDatePart = (value: number, length = 2) => value.toString().padStart(length, "0");

export const getLocalTimezoneId = () => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone ?? null;
};

export const isValidTimezoneId = (timezoneId: string | null | undefined) => {
  return Boolean(timezoneId && getDateTimeFormatter(timezoneId));
};

/**
 * Converts a UTC instant into a timezone-local wall-clock value. API timestamps
 * without an explicit offset are treated as UTC because EM2 stores audit dates
 * in UTC. The returned value intentionally has no offset so it can be rendered
 * without the browser applying a second timezone conversion.
 */
export const toTimezoneWallClock = (date: Date | string | null | undefined, timezoneId: string | null | undefined) => {
  if (!date || !timezoneId) {
    return null;
  }

  const instant = parseUtcInstant(date);

  if (!instant) {
    return null;
  }

  const formatter = getDateTimeFormatter(timezoneId);

  if (!formatter) {
    return null;
  }

  try {
    const parts = formatter.formatToParts(instant);

    const year = getDatePart(parts, "year");
    const month = getDatePart(parts, "month");
    const day = getDatePart(parts, "day");
    const hour = getDatePart(parts, "hour");
    const minute = getDatePart(parts, "minute");
    const second = getDatePart(parts, "second");

    if ([year, month, day, hour, minute, second].some(Number.isNaN)) {
      return null;
    }

    return `${padDatePart(year, 4)}-${padDatePart(month)}-${padDatePart(day)}T${padDatePart(hour)}:${padDatePart(
      minute,
    )}:${padDatePart(second)}`;
  } catch {
    return null;
  }
};

export const getTimezoneOffsetInMinutes = (timezoneId: string | null | undefined, date = new Date()) => {
  if (!timezoneId) {
    return null;
  }

  const formatter = getDateTimeFormatter(timezoneId);

  if (!formatter) {
    return null;
  }

  try {
    const parts = formatter.formatToParts(date);

    const year = getDatePart(parts, "year");
    const month = getDatePart(parts, "month");
    const day = getDatePart(parts, "day");
    const hour = getDatePart(parts, "hour");
    const minute = getDatePart(parts, "minute");
    const second = getDatePart(parts, "second");

    if ([year, month, day, hour, minute, second].some(Number.isNaN)) {
      return null;
    }

    const utcTimeForTimezoneWallClock = Date.UTC(year, month - 1, day, hour, minute, second);

    return Math.round((utcTimeForTimezoneWallClock - date.getTime()) / 60000);
  } catch {
    return null;
  }
};

export const getTimezoneDifferenceInMinutes = (
  eventTimezoneId: string | null | undefined,
  localTimezoneId: string | null | undefined = getLocalTimezoneId(),
  date = new Date(),
) => {
  const eventOffset = getTimezoneOffsetInMinutes(eventTimezoneId, date);
  const localOffset = getTimezoneOffsetInMinutes(localTimezoneId, date);

  if (eventOffset === null || localOffset === null) {
    return null;
  }

  return eventOffset - localOffset;
};

export const getTimezoneDifferenceAtUtcTimestamp = (
  eventTimezoneId: string | null | undefined,
  localTimezoneId: string | null | undefined,
  date: Date | string,
) => {
  const instant = parseUtcInstant(date);

  return instant ? getTimezoneDifferenceInMinutes(eventTimezoneId, localTimezoneId, instant) : null;
};

export const formatTimezoneDifference = (differenceInMinutes: number | null | undefined) => {
  if (differenceInMinutes === null || differenceInMinutes === undefined) {
    return "Unable to calculate";
  }

  if (differenceInMinutes === 0) {
    return "0 hours";
  }

  const sign = differenceInMinutes > 0 ? "+" : "-";
  const absoluteDifference = Math.abs(differenceInMinutes);
  const hours = Math.floor(absoluteDifference / 60);
  const minutes = absoluteDifference % 60;

  if (hours === 0) {
    return `${sign}${minutes} ${minutes === 1 ? "minute" : "minutes"}`;
  }

  const hoursLabel = `${hours} ${hours === 1 ? "hour" : "hours"}`;

  if (minutes === 0) {
    return `${sign}${hoursLabel}`;
  }

  return `${sign}${hoursLabel} ${minutes} ${minutes === 1 ? "minute" : "minutes"}`;
};
